import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import Tooltip, { TooltipContent, TooltipTrigger } from "../../../Overlays/Tooltip";
import useModal from "../../../../Hooks/useModal";
import SkynetCopy from "../../../Fundamentals/SkynetCopy";
import SkynetIcon from "../../../Fundamentals/SkynetIcon";
import Modals from "../../../../Constants/modals";
import helpers from "../../../../Utils/helpers";
import Card, { CardContainer } from "../../../Card/Card";
import customerActions from "../../../../Actions/customerActions";
import eventsActions from "../../../../Actions/InternalCustomerAdmin/eventsActions";
import useUserAccess from "../../../../Hooks/useUserAccess";
import { UserRoles } from "../../../../Constants/userRoles";
import routes from '../../../../Constants/Customer/customerRoutes';
import VonEmployeesModalManager from "../../../ModalManagers/VonEmployeesModalManager";
import ExpandableTable from "../../../Fundamentals/Tables/ExpandedableTable";
import SortableTableHeader from "../../../Fundamentals/Tables/SortableTableHeader";
import useSortingAndFiltering from "../../../../Hooks/useSortingAndFiltering";
import {Dropdown as DropdownJS} from "../../../FormComponents/Dropdown";
import { VonEmployeeTypes } from '../../../../Constants/VON/EmployeeForm/vonEmployeeTypes';
import RepublishEventModal from "../../../Modals/RepublishEventModal/RepublishEventModal";
import {productId} from "../../../../Constants/producs";
import {VonAccessRights, VonAccessRightsRoles} from "../../../../Constants/VON/EmployeeForm/vonAccessRights";
import {Checkbox} from '../../../FormComponents/Checkbox';

import type { Dispatch } from 'redux';
import type { Employee, Role, RootState, UserIconProps } from './VonEmployees.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const Dropdown = DropdownJS as unknown as React.ComponentType<any>;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyDispatch = Dispatch<any>;

function UserIcon({ employee }: UserIconProps) {
    const { t } = useTranslation();
    const icons = [];

    if (employee.isLockedOut) {
        icons.push({
            icon: "vismaicon-lock",
            title: "vonaccountscontainer.locked"
        });
    }

    if (employee.consultantId !== null && employee.consultantId !== undefined) {
        icons.push({
            icon: "vismaicon-user-connection",
            title: "vonaccountscontainer.consultant"
        });
    }

    if (employee.employeeType === VonEmployeeTypes.Auditor) {
        icons.push({
            icon: "vismaicon-user-reports",
            title: "vonaccountscontainer.auditor"
        });
    }
    
    if (employee.employeeType === VonEmployeeTypes.BankruptcyTrustee) {
        icons.push({
            icon: "vismaicon-user-settings",
            title: "vonaccountscontainer.bankruptcyTrustee"
        });
    }

    if (icons.length === 0) return null;

    return (
        <div className="d-flex align-items-center">
            {icons.map(({ icon, title }, idx) => (
                <Tooltip key={`${icon}-${idx}`} options={{}}>
                    <TooltipTrigger attributes={{}} className="" style={{}}>
                        <SkynetIcon icon={`${icon} vismaicon-sm mr-8`} title={t(title) ?? undefined} onClick={undefined} disabled={false} />
                    </TooltipTrigger>
                    <TooltipContent>
                        <div>{t(title)}</div>
                    </TooltipContent>
                </Tooltip>
            ))}
        </div>
    );
}

const defaultComparer = (a: Employee, b: Employee) =>
        (b.isPrimaryContact ? 1 : 0) - (a.isPrimaryContact ? 1 : 0) || (a.name || '').localeCompare(b.name || '');

const VonEmployees = () => {
    const { t } = useTranslation();
    const { pathId } = useParams<{ pathId: string }>();
    const vonAccounts = useSelector((state: RootState) => state.CustomerReducer.vonAccounts);
    const dataLoading = useSelector((state: RootState) => state.CommonReducer.dataLoading.vonAccounts);
    const isRepublishing = useSelector((state: RootState) => state.CommonReducer.dataLoading.resendEmployeeEvent);
    const vonProducts = useSelector((state: RootState) => state.CustomerReducer.vonProducts);
    const { hasReadWriteAccess } = useUserAccess();

    const dispatch = useDispatch<AnyDispatch>();

    const [showAdmins, setShowAdmins] = useState<boolean>(false);
    const [selectedEmployeeType, setSelectedEmployeeType] = useState<string | number>('');
    const [showInactive, setShowInactive] = useState<boolean>(false);
    const [groupedVonAccounts, setGroupedVonAccounts] = useState<Record<string, Record<string, Employee[]>>>({});
    const [republishContext, setRepublishContext] = useState<{ employeeId: string; applicationId: string } | null>(null);

    const { openModal, closeModal, isModalOpen } = useModal();
    const navigate = useNavigate();

    const vonAccountsWithAccessRights = (vonAccounts ?? []).map(employee => ({
        ...employee,
        vonAccessRights: getEmployeeAccessRightsLabel(employee)
    }));

    const [searchVonAccountInput, setSearchVonAccountInput] = useState('');

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const {filteredAndSortedList, sortKey, sortOrder, handleSortChange} = (useSortingAndFiltering as any)({
        list: vonAccountsWithAccessRights,
        searchText: searchVonAccountInput,
        searchableKeys: ['name', 'email', 'userId'],
        customFilterKeys: [
            ...(showAdmins ? [{ key: 'isVonAdmin', condition: (value: unknown) => value === true }] : []),
            ...(!showInactive ? [{ key: 'vonAccessRights', condition: (value: unknown) => value !== t('vonAccessRights.softDelete') && value !== t('vonAccessRights.inactive') }] : []),
            ...(typeof selectedEmployeeType === 'number' ? [{ key: 'employeeType', condition: (value: unknown) => value === selectedEmployeeType }] : [])
        ],
        defaultComparer
    }) as { filteredAndSortedList: Employee[]; sortKey: string; sortOrder: 'asc' | 'desc'; handleSortChange: (key: string) => void };

    const handleRepublishEvent = (employeeId: string, applicationId: string) => {
        setRepublishContext({ employeeId, applicationId });
        openModal(Modals.RepublishEventModal);
    };

    const confirmRepublishEvent = () => {
        if (republishContext && !isRepublishing) {
            dispatch(eventsActions.resendApplicationAddedToEmployee(
                republishContext.employeeId,
                republishContext.applicationId,
                () => {
                    closeModal();
                    setRepublishContext(null);
                }
            ));
        }
    };

    useEffect(() => {
        return () => {
            dispatch(customerActions.setSearchVonAccountInput(null));
        }
    }, [dispatch])

    useEffect(() => {
        setGroupedVonAccounts(groupVonAccounts(vonAccounts ?? []));
    }, [vonAccounts])

    const groupVonAccounts = (vonAccounts: Employee[]): Record<string, Record<string, Employee[]>> => {
        const result: Record<string, Record<string, Employee[]>> = {};
        vonAccounts.forEach(account => {
            account.roles.forEach(role => {
                const productKey = role.specifiedProductName !== null && role.specifiedProductName !== undefined && role.specifiedProductName !== ''
                    ? `${role.productName} - ${role.specifiedProductName}`
                    : role.productName ?? 'n/a';
                result[productKey] ??= {};
                result[productKey][role.role] ??= [];
                result[productKey][role.role].push({ ...account, role });
            });
        });
        return result;
    }


    const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchVonAccountInput(event.target.value);
    }

    const onEmployeeTypeChange = (val: string | number) => {
        setSelectedEmployeeType(val === '' ? '' : Number(val));
    }


    function getEmployeeAccessRightsLabel(employee: Employee): string {
        const role = employee.roles.find(role => role.service === productId.vismaonline);
        if (role?.serviceRole !== null && role?.serviceRole !== undefined && role.serviceRole !== '') {
            const rolesMap = VonAccessRightsRoles as Record<string, string>;
            const id: string | undefined = rolesMap[role.serviceRole];
            const entry = Object.entries(VonAccessRights).find(([_, value]) => value === id);
            if (entry !== null && entry !== undefined) return t(`vonAccessRights.${entry[0].toLowerCase()}`);
            return t(`vonAccessRights.${id !== null && id !== undefined ? id.toLowerCase() : 'unknown'}`);
        }
        if (employee.softDelete) return t('vonAccessRights.softDelete');
        return t('vonAccessRights.inactive');
    }

    const CardHeaderTitle = (Card as unknown as { Header: { Title: React.ComponentType<{ children: React.ReactNode }> } }).Header.Title;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/strict-boolean-expressions
    const hasLicensedProducts = (vonProducts?.filter((p: any) => p.licenses).length ?? 0) > 0;

    return <CardContainer>
        <Card>
            <Card.CHeader disabled={false} onOpenChanged={(_open: boolean) => { /* noop */ }}>
                <CardHeaderTitle>{t("customernavigation.users")}</CardHeaderTitle>
            </Card.CHeader>
            <Card.Content isLoading={dataLoading} hasNoData={false}>
                <>
                    <div className={`mb-10 d-flex gap-8 ${Boolean(hasReadWriteAccess(UserRoles.CompanyUser)) ? '' : 'disabled'}`}>
                        <button className='btn btn-default btn-sm' onClick={() => openModal(Modals.NewVonEmployeeModal)}>{t('vonaccountscontainer.createnewuser')}</button>
                        <button className='btn btn-default btn-sm' disabled={!hasLicensedProducts} onClick={() => { Promise.resolve(navigate('licenses')).catch(() => {}); }}>{t('vonaccountscontainer.administerlicenses')}</button>
                    </div>
                    <div className="d-flex gap-8 align-items-center mb-10">
                        <button className='btn btn-default btn-sm'
                                onClick={() => setShowAdmins(!showAdmins)}>{showAdmins ? t("vonaccountscontainer.showall") : t("vonaccountscontainer.showadmins")}
                        </button>
                        <div className="form-group mb-0">
                            <Dropdown
                                options={[
                                    { id: '', name: t('vonaccountscontainer.allTypes') },
                                    ...Object.entries(VonEmployeeTypes).map(([key, value]) => ({
                                        id: value,
                                        name: t(`vonEmployeeTypes.${key.toLowerCase()}`),
                                    }))
                                ]}
                                value={selectedEmployeeType}
                                onChange={onEmployeeTypeChange}
                            />
                        </div>
                    </div>
                    <div className="mb-10 d-flex justify-content-between align-items-center">
                        <div className="d-flex col-md-8 p-0">
                            <input id="employeefilter" placeholder={t("vonaccountscontainer.searchfield") ?? ''} className="form-control" type="text"
                                   autoFocus value={searchVonAccountInput} onChange={onChange} />
                        </div>
                        <div className="d-flex flex-shrink-0">
                            <Checkbox
                                label={t("vonaccountscontainer.showInactive")}
                                checked={showInactive}
                                onChange={(checked: boolean) => setShowInactive(checked)}
                                disabled={false}
                                className=""
                                labelClassName=""
                                id="show-inactive-checkbox"
                            />
                        </div>
                    </div>
                    <ExpandableTable>
                        <thead>
                            <tr>
                            <SortableTableHeader 
                                sortColumn="name"
                                currentSortKey={sortKey}
                                sortOrder={sortOrder}
                                onSort={handleSortChange}
                                showInactiveIcon={true}
                            >
                                {t("vonaccountscontainer.name")}
                            </SortableTableHeader>
                            <SortableTableHeader 
                                sortColumn="email"
                                currentSortKey={sortKey}
                                sortOrder={sortOrder}
                                onSort={handleSortChange}
                                showInactiveIcon={true}
                            >
                                {t("vonaccountscontainer.email")}
                            </SortableTableHeader>
                            <SortableTableHeader 
                                sortColumn="vonAccessRights"
                                currentSortKey={sortKey}
                                sortOrder={sortOrder}
                                onSort={handleSortChange}
                                showInactiveIcon={true}
                            >
                                {t("vonaccountscontainer.vonAccessRights")}
                            </SortableTableHeader>
                            <SortableTableHeader 
                                sortColumn="latestLoginTime"
                                currentSortKey={sortKey}
                                sortOrder={sortOrder}
                                onSort={handleSortChange}
                                showInactiveIcon={true}
                            >
                                {t("vonaccountinfomodal.latestlogin")}
                            </SortableTableHeader>
                            <SortableTableHeader>
                                {t("common.edit")}
                            </SortableTableHeader>
                            </tr>
                        </thead>
                        <ExpandableTable.Body>
                            {filteredAndSortedList
                                ?.map((employee: Employee) => {
                                    return (
                                        <ExpandableTable.Row key={employee.id}>
                                            <ExpandableTable.Row.Expandable {...{} as React.ComponentProps<typeof ExpandableTable.Row.Expandable>}>
                                                <td className={`d-flex align-items-center ${employee.isPrimaryContact ? ' bold' : ''}`}>
                                                    <UserIcon employee={employee} />
                                                    <span className="ms-1">{employee.name}</span>
                                                    <SkynetCopy label={t("vonaccountscontainer.name")} value={employee.name} toggleCopy={undefined} classList="">{null}</SkynetCopy>
                                                </td>
                                                <td>{employee.email}
                                                    <SkynetCopy label={t("vonaccountscontainer.email")} value={employee.email} toggleCopy={undefined} classList="">{null}</SkynetCopy>
                                                </td>
                                                <td>
                                                    {employee.vonAccessRights}
                                                </td>
                                                <td> {helpers.formatDateAndTime(employee.latestLoginTime)}
                                                </td>
                                                <td>
                                                    <div className="d-flex justify-content-center">
                                                        <SkynetIcon icon={'vismaicon-edit'} title={t("common.edit") ?? undefined} onClick={() => { Promise.resolve(navigate(routes.customer(pathId, routes.EditUser(employee.id)))).catch(() => {}); }} disabled={false} />
                                                    </div>
                                                </td>
                                            </ExpandableTable.Row.Expandable>
                                            <ExpandableTable.Row.Expanded {...{} as React.ComponentProps<typeof ExpandableTable.Row.Expanded>}>
                                                <ExpandableTable.Row>
                                                    <ExpandableTable.Row.Expandable {...{} as React.ComponentProps<typeof ExpandableTable.Row.Expandable>}>
                                                        <td>Services</td>
                                                        <td className="text-left">{t("vonaccountscontainer.role")}</td>
                                                        <td colSpan={100} className="text-left">{t("vonaccountscontainer.expiryDate")}</td>
                                                    </ExpandableTable.Row.Expandable>
                                                    <ExpandableTable.Row.Expanded {...{} as React.ComponentProps<typeof ExpandableTable.Row.Expanded>}>
                                                {employee.roles.sort((a: Role, b: Role) => a.productName.localeCompare(b.productName)).map((role: Role, rIdx: number) => {
                                                            return (
                                                        <tr key={`${role.id}-${rIdx}`} className="mb-10">
                                                                    <td>
                                                                        <strong>{role.productName}</strong>
                                                                        {role.specifiedProductName !== undefined && role.specifiedProductName !== '' && ` - ${role.specifiedProductName}`}
                                                                    </td>
                                                                    <td>
                                                                       {role.role}
                                                                    </td>
                                                                    <td>
                                                                        {role.expiryDate !== null && role.expiryDate !== undefined && helpers.formatDateAndTime(role.expiryDate)}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <button className="btn btn-link btn-sm p-0" onClick={() => handleRepublishEvent(employee.id, role.applicationId)}>
                                                                            Republish event
                                                                        </button>
                                                                    </td>
                                                                </tr>
                                                            )
                                                        })}
                                                    </ExpandableTable.Row.Expanded>
                                                </ExpandableTable.Row>
                                                <ExpandableTable.Row>
                                                    <ExpandableTable.Row.Expandable {...{colSpan: 100} as React.ComponentProps<typeof ExpandableTable.Row.Expandable>}>
                                                        <td>Additional info</td>
                                                    </ExpandableTable.Row.Expandable>
                                                    <ExpandableTable.Row.Expanded {...{} as React.ComponentProps<typeof ExpandableTable.Row.Expanded>}>
                                                        <tr>
                                                            <td colSpan={30}>
                                                                <strong>{t('vonaccountinfomodal.userid')}:</strong> {employee.userId}
                                                                <SkynetCopy label={t("vonaccountinfomodal.userid")} value={employee.userId} toggleCopy={undefined} classList="">{null}</SkynetCopy>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td colSpan={30}>
                                                                <strong>{t('vonaccountinfomodal.connectuserid')}:</strong> {employee.connectUserId}
                                                                <SkynetCopy label={t("vonaccountinfomodal.connectuserid")} value={employee.connectUserId} toggleCopy={undefined} classList="">{null}</SkynetCopy>
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <td colSpan={30}>
                                                                <strong>{t('vonaccountinfomodal.employeeId')}:</strong> {employee.id}
                                                                <SkynetCopy label={t("vonaccountinfomodal.employeeId")} value={employee.id} toggleCopy={undefined} classList="">{null}</SkynetCopy>
                                                            </td>
                                                        </tr>
                                                    </ExpandableTable.Row.Expanded>
                                                </ExpandableTable.Row>
                                            </ExpandableTable.Row.Expanded>
                                        </ExpandableTable.Row>)
                                })}
                        </ExpandableTable.Body>
                    </ExpandableTable>
                </>
            </Card.Content>
        </Card>
        <Card>
            <Card.CHeader disabled={false} onOpenChanged={(_open: boolean) => { /* noop */ }}>
                <CardHeaderTitle>{t("vonaccountscontainer.userByProductAndRole")}</CardHeaderTitle>
            </Card.CHeader>
            <Card.Content isLoading={dataLoading} hasNoData={false}>
                <ExpandableTable>
                    <thead>
                        <tr>
                            <th className="text-left">{t('common.product')}</th>
                        </tr>
                    </thead>
                    <ExpandableTable.Body>
                        {Object.entries(groupedVonAccounts).map(([product, roles]) => (
                            <ExpandableTable.Row key={product}>
                                <ExpandableTable.Row.Expandable {...{} as React.ComponentProps<typeof ExpandableTable.Row.Expandable>}>
                                    <td>
                                        {product}
                                        <span className="ml-8 badge">
                                            {Object.values(roles).reduce((sum, accounts) => sum + accounts.length, 0)}
                                        </span>
                                    </td>
                                </ExpandableTable.Row.Expandable>
                                <ExpandableTable.Row.Expanded {...{} as React.ComponentProps<typeof ExpandableTable.Row.Expanded>}>
                                    {Object.entries(roles).map(([roleName, accounts]) => (
                                        <ExpandableTable.Row key={roleName}>
                                            <ExpandableTable.Row.Expandable {...{colSpan: 100} as React.ComponentProps<typeof ExpandableTable.Row.Expandable>}>
                                                <td>
                                                    {roleName}
                                                    <span className="ml-8 badge">{accounts.length}</span>
                                                </td>
                                            </ExpandableTable.Row.Expandable>
                                            <ExpandableTable.Row.Expanded>
                                                {accounts.map((account, aIdx) => (
                                                    <tr key={`${account.id}-${aIdx}`}>
                                                        <td>
                                                            <div>
                                                                <UserIcon employee={account} />
                                                                <strong>{t("vonaccountscontainer.name")}:</strong> {account.name}
                                                                <SkynetCopy label={t("vonaccountscontainer.name")} value={account.name} toggleCopy={undefined} classList="">{null}</SkynetCopy>
                                                            </div>
                                                            <div>
                                                                <strong>{t('vonaccountscontainer.email')}:</strong> {account.email}
                                                                <SkynetCopy label={t("vonaccountscontainer.email")} value={account.email} toggleCopy={undefined} classList="">{null}</SkynetCopy>
                                                            </div>
                                                            <div>
                                                                <strong>{t('vonaccountinfomodal.userid')}:</strong> {account.userId}
                                                                <SkynetCopy label={t("vonaccountinfomodal.userid")} value={account.userId} toggleCopy={undefined} classList="">{null}</SkynetCopy>
                                                            </div>
                                                            <div>
                                                                <strong>{t('vonaccountinfomodal.connectuserid')}:</strong> {account.connectUserId}
                                                                <SkynetCopy label={t("vonaccountinfomodal.connectuserid")} value={account.connectUserId} toggleCopy={undefined} classList="">{null}</SkynetCopy>
                                                            </div>
                                                            <div>
                                                                <strong>{t('vonaccountinfomodal.employeeId')}:</strong> {account.id}
                                                                <SkynetCopy label={t("vonaccountinfomodal.employeeId")} value={account.id} toggleCopy={undefined} classList="">{null}</SkynetCopy>
                                                            </div>
                                                            {<div>
                                                                <strong>{t('vonaccountinfomodal.latestlogin')}:</strong> {helpers.formatDateAndTime(account.latestLoginTime)}
                                                            </div>}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </ExpandableTable.Row.Expanded>
                                        </ExpandableTable.Row>
                                    ))}
                                </ExpandableTable.Row.Expanded>
                            </ExpandableTable.Row>
                        ))}
                    </ExpandableTable.Body>
                </ExpandableTable>
            </Card.Content>
        </Card>
        <VonEmployeesModalManager />
        {isModalOpen(Modals.RepublishEventModal) && (
            <RepublishEventModal
                title={t('republishEventModal.title')}
                description={t('republishEventModal.description')}
                onConfirm={confirmRepublishEvent}
                isLoading={isRepublishing}
            />
        )}
    </CardContainer>
}

export default VonEmployees;

